import { CommonReport } from "components/reports/show/markup";
import Select, {
    customFilterSelectStyles,
    defaultMultipleSelectProps,
    OptionWithFederalSubject,
    ValueContainer,
} from "components/shared/select";
import { bidEmptyMileageTypes, reportColumnAggregateTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import React from "react";
import { numbersListWithDeleted } from "sources/books/trucks";
import { getRegionsByName } from "sources/map";

const regionSelectStyles = {
    ...customFilterSelectStyles,
    control: (styles) => ({
        ...customFilterSelectStyles.control(styles),
        height: "28px",
        minHeight: "28px",
    }),
    valueContainer: (styles) => ({
        ...styles,
        flexWrap: "nowrap",
        height: "26px",
        lineHeight: "18px",
        paddingBottom: 0,
        paddingTop: 0,
    }),
    input: (styles) => ({ ...styles, height: "17px", margin: 0, paddingBottom: 0, paddingTop: 0 }),
    indicatorsContainer: (styles) => ({ ...customFilterSelectStyles.indicatorsContainer(styles), height: "26px" }),
    dropdownIndicator: (styles) => ({ ...styles, padding: "4px" }),
    clearIndicator: (styles) => ({ ...styles, padding: "4px" }),
};

const RegionColumnFilter = ({ onFilteredChange, column, selectedRegion, onRegionChange }) => {

    return (
        <Select
            bWrapper="row"
            components={{ Option: OptionWithFederalSubject }}
            inputProps={{ name: "region" }}
            menuPortalTarget={document.body}
            minimumInputLength={2}
            onChange={({ value, option }) => {
                const region = value ? { id: option.id, type: option.type } : null;

                onFilteredChange({
                    id: column.id,
                    value: region,
                    filterType: column.filterType,
                });
                onRegionChange(region);
            }}
            propName="name"
            styles={regionSelectStyles}
            url={getRegionsByName}
            value={selectedRegion?.id || null}
            wrapper="book-field"
        />
    );
};

export default class EmptyMileageReport extends CommonReport {
    footerVal = (rows, column) => {
        const { aggregateType, clrType } = column;
        if (
            !aggregateType ||
            aggregateType === reportColumnAggregateTypesEnum.None ||
            !this.numericClrTypes.includes(clrType)
        ) {
            return null;
        }

        const { total, count } = rows.reduce(
            (acc, row) => {
                if (row.isGrouped) {
                    return acc;
                }

                const value = row.values[columnName(column)];
                if (Number.isFinite(value)) {
                    acc.total += value;
                    acc.count += 1;
                }

                return acc;
            },
            { total: 0, count: 0 },
        );

        switch (aggregateType) {
            case reportColumnAggregateTypesEnum.Average:
                return count ? <span>{formatValue(Math.round(total / count), clrType)}</span> : null;
            case reportColumnAggregateTypesEnum.Sum:
                return <span>{formatValue(total, clrType)}</span>;
            default:
                return null;
        }
    };

    cellProps = (column) => {
        return {
            Header: column.label,
            accessor: columnName(column),
            show: !column.isAdditionalColumn,
            Cell: (props) => {
                switch (props.column.id) {
                    case "bidId":
                        return (
                            <a
                                data-bidid={props.value}
                                href={paths.bidShow("bid", props.value)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {props.value}
                            </a>
                        );
                    case "carId":
                        return (
                            <a
                                data-carid={props.value}
                                href={paths.trucksShow(props.value)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {props.row.values.carNumber}
                            </a>
                        );
                    default:
                        return <div className="long-text">{formatValue(props.value, column.clrType)}</div>;
                }
            },
            Footer: (info) => this.footerVal(info.flatRows, column),
            ...this.typeSpecificProps(column),
        };
    };

    typeSpecificProps = (column) => {
        const restProps = typeSpecificProps(column);

        switch (column.propertyName) {
            case "CarId":
                return {
                    ...restProps,
                    Filter: reportFilterWrapper((props) => (
                        <SelectColumnFilter
                            {...props}
                            selectProps={{
                                ...defaultMultipleSelectProps,
                                url: numbersListWithDeleted,
                                inputProps: { name: "carIds" },
                                propName: "number",
                                components: { ValueContainer },
                            }}
                        />
                    )),
                    filter: (rows, _id, filterValue) => {
                        if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                            return rows;
                        }
                        return rows.filter((row) => filterValue.value.includes(row.values.carId));
                    },
                };
            case "MileageTypeDisplayName":
                return {
                    ...restProps,
                    Filter: reportFilterWrapper((props) => (
                        <SelectColumnFilter
                            {...props}
                            selectProps={{
                                ...defaultMultipleSelectProps,
                                options: bidEmptyMileageTypes.map((option) => ({
                                    label: option.label,
                                    value: option.label,
                                })),
                                inputProps: { name: "bidEmptyMileageTypes" },
                                components: { ValueContainer },
                            }}
                        />
                    )),
                    filter: (rows, _id, filterValue) => {
                        if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                            return rows;
                        }
                        return rows.filter((row) => filterValue.value.includes(row.values.mileageTypeDisplayName));
                    },
                };
            case "State":
                return {
                    ...restProps,
                    Filter: reportFilterWrapper((props) => (
                        <RegionColumnFilter
                            {...props}
                            selectedRegion={this.props.extraParams?.region}
                            onRegionChange={(region) => this.props.getItem({ region })}
                        />
                    )),
                    filter: (rows) => rows,
                };
            default:
                return restProps;
        }
    };

    tableExtraProps = () => {
        const hiddenColumns = (this.props.data?.columns || [])
            .filter((column) => column.label === null)
            .map(columnName);

        return {
            initialState: {
                hiddenColumns: ["carNumber", ...hiddenColumns],
            },
        };
    };
}
